import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SseProvider } from "./context/SseContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";

// iter 130 perf — keep LoginPage + Layout in the initial bundle (Layout
// loads on every authenticated session, LoginPage on first paint). Every
// other page (including DashboardPage) is lazy-loaded so the initial
// bundle stays tiny. React.lazy + Suspense ships each page as its own
// chunk that's fetched on first navigation.
import LoginPage from "./pages/LoginPage";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));

const CandidatesPage = lazy(() => import("./pages/CandidatesPage"));
const CandidateDetailPage = lazy(() => import("./pages/CandidateDetailPage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const BDEManagerJobDetailPage = lazy(() => import("./pages/BDEManagerJobDetailPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const InterviewsPage = lazy(() => import("./pages/InterviewsPage"));
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const InterviewDetailPage = lazy(() => import("./pages/InterviewDetailPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const TeamManagementPage = lazy(() => import("./pages/TeamManagementPage"));
const TeamDetailPage = lazy(() => import("./pages/TeamDetailPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const WorkordersPage = lazy(() => import("./pages/WorkordersPage"));
const TimesheetsPage = lazy(() => import("./pages/TimesheetsPage"));
const LaptopsPage = lazy(() => import("./pages/LaptopsPage"));
const EmailRequestsPage = lazy(() => import("./pages/EmailRequestsPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const AnalysisPage = lazy(() => import("./pages/AnalysisPage"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const BdeShortlistedCandidatesPage = lazy(() => import("./pages/BdeShortlistedCandidatesPage"));
const TeamLeadShortlistedCandidatesPage = lazy(() => import("./pages/TeamLeadShortlistedCandidatesPage"));
const SetupPage = lazy(() => import("./pages/SetupPage"));
const EmailSourcingPage = lazy(() => import("./pages/EmailSourcingPage"));
const GmailSourcingPage = lazy(() => import("./pages/GmailSourcingPage"));
const SemanticSearchPage = lazy(() => import("./pages/SemanticSearchPage"));
const GmailSyncAdminPage = lazy(() => import("./pages/GmailSyncAdminPage"));
const GmailSyncControlCenter = lazy(() => import("./pages/GmailSyncControlCenter"));
const GamificationDashboardPage = lazy(() => import("./pages/GamificationDashboardPage"));
const AdminGamificationPage = lazy(() => import("./pages/AdminGamificationPage"));
const SmartDashboard = lazy(() => import("./pages/SmartDashboard"));
const LeadsGenPage = lazy(() => import("./pages/LeadsGenPage"));
const ICFsPage = lazy(() => import("./pages/ICFsPage"));
const LeadsGenDashboard = lazy(() => import("./pages/LeadsGenDashboard"));
const LeadsGenActivity = lazy(() => import("./pages/LeadsGenActivity"));
const MISLeadsGen = lazy(() => import("./mis/MISLeadsGen"));
const RecruiterDashboard = lazy(() => import("./pages/RecruiterDashboard"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CustomEntityPage = lazy(() => import("./pages/CustomEntityPage"));

// iter 191 — MIS Portal (isolated shell, Admin/Super Admin/MIS role only)
const MISLayout = lazy(() => import("./mis/MISLayout"));
const MISDashboard = lazy(() => import("./mis/MISDashboard"));
const MISB2BTeam = lazy(() => import("./mis/MISB2BTeam"));
const MISLeaderboard = lazy(() => import("./mis/MISLeaderboard"));
const MISB2CTeam = lazy(() => import("./mis/MISB2CTeam"));
const MISTuringTeam = lazy(() => import("./mis/MISTuringTeam"));
const MISMicro1Team = lazy(() => import("./mis/MISMicro1Team"));
const MISICFTeam = lazy(() => import("./mis/MISICFTeam"));
// iter 211 — MIS Internal Screening + RecruitPro Inhouse Developers
const MISInternalScreening = lazy(() => import("./mis/MISInternalScreening"));
const InhouseDevelopersPage = lazy(() => import("./pages/InhouseDevelopersPage"));
// iter 203 — MIS Turing role pages (inside the RecruitPro shell)
const TuringDashboardPage = lazy(() => import("./pages/TuringDashboardPage"));
const TuringSubmissionsPage = lazy(() => import("./pages/TuringSubmissionsPage"));
const MicroSubmissionsPage = lazy(() => import("./pages/MicroSubmissionsPage"));
// iter 205 — Internal Screening role pages (inside RecruitPro shell)
const ScreeningDashboardPage = lazy(() => import("./pages/ScreeningDashboardPage"));
const ScreeningPage = lazy(() => import("./pages/ScreeningPage"));
// iter 202 — ICF Portal (Turing reviewers)
const ICFLayout = lazy(() => import("./icf/ICFLayout"));
const ICFDashboard = lazy(() => import("./icf/ICFDashboard"));

// iter 193 — B2C Portal (isolated shell, Admin/Super Admin/b2c_* roles only)
const B2CLayout = lazy(() => import("./b2c/B2CLayout"));
const B2CDashboard = lazy(() => import("./b2c/B2CDashboard"));
const B2CUsers = lazy(() => import("./b2c/B2CUsers"));
const B2CDevelopers = lazy(() => import("./b2c/B2CDevelopers"));
const B2CInterviews = lazy(() => import("./b2c/B2CInterviews"));
const B2CDailyUpdates = lazy(() => import("./b2c/B2CDailyUpdates"));

// Suspense fallback used for every lazy-loaded route — tiny inline spinner,
// no full-screen flash on quick chunks.
const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const routerLocation = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // iter 191 — MIS role is locked into the MIS portal only
  if (user?.role === 'mis') {
    return <Navigate to="/mis" replace />;
  }
  
  // iter 203 — MIS Turing role is locked into its Turing pages
  // iter249 — plus the ICF's workflow page
  if (user?.role === 'mis_turing' && routerLocation.pathname !== '/icfs') {
    return <Navigate to="/turing/dashboard" replace />;
  }
  
  // iter 205 — Internal Screening role is locked into its two pages
  if (user?.role === 'internal_screening') {
    return <Navigate to="/screening/dashboard" replace />;
  }
  
  // iter 202 — ICF role is locked into the ICF portal only
  // iter220 — main-shell paths (jobs/candidates/pipeline) are preserved and
  // re-mounted inside the ICF shell so shared pages' navigate() calls work.
  if (user?.role === 'icf') {
    const p = routerLocation.pathname;
    if (/^\/(jobs|candidates|pipeline)/.test(p)) {
      return <Navigate to={`/icf${p}${routerLocation.search}`} replace />;
    }
    return <Navigate to="/icf" replace />;
  }
  
  // iter 193 — B2C roles are locked into the B2C portal only
  if (user?.role?.startsWith('b2c_')) {
    return <Navigate to="/b2c" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// iter 193 — B2C Portal route guard (Admin / Super Admin / b2c_* roles only)
const B2CProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!(user?.role === 'admin' || user?.role === 'super_admin' || user?.role?.startsWith('b2c_'))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// iter 191 — MIS Portal route guard (Admin / Super Admin / MIS role only)
const MISProtectedRoute = ({ children, allowRole }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const allowed = allowRole === 'icf' ? ['admin', 'super_admin', 'icf'] : ['admin', 'super_admin', 'mis'];
  if (!allowed.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// iter 203 — MIS Turing route guard (mis_turing role only, renders inside the
// main RecruitPro shell)
// iter239 — MIS Turing Team page for admin-granted users (server-enforced flag)
const TuringTeamAccessRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.turing_team_access && !['admin', 'super_admin', 'mis'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Layout>{children}</Layout>;
};

const MISTuringProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'mis_turing') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// iter 205 — Internal Screening route guard (internal_screening role only)
const ScreeningProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'internal_screening') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Tab-Protected Route Component - checks profile-based tab access
const TabProtectedRoute = ({ children, tabName }) => {
  const routerLocation = useLocation();
  const { isAuthenticated, loading, hasTabAccess, permissions, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // iter 191 — MIS role is locked into the MIS portal only
  if (user?.role === 'mis') {
    return <Navigate to="/mis" replace />;
  }
  
  // iter 203 — MIS Turing role is locked into its two Turing pages
  if (user?.role === 'mis_turing') {
    return <Navigate to="/turing/dashboard" replace />;
  }
  
  // iter 205 — Internal Screening role is locked into its two pages
  if (user?.role === 'internal_screening') {
    return <Navigate to="/screening/dashboard" replace />;
  }
  
  // iter 202 — ICF role is locked into the ICF portal only
  // iter220 — main-shell paths (jobs/candidates/pipeline) are preserved and
  // re-mounted inside the ICF shell so shared pages' navigate() calls work.
  if (user?.role === 'icf') {
    const p = routerLocation.pathname;
    if (/^\/(jobs|candidates|pipeline)/.test(p)) {
      return <Navigate to={`/icf${p}${routerLocation.search}`} replace />;
    }
    return <Navigate to="/icf" replace />;
  }
  
  // iter 193 — B2C roles are locked into the B2C portal only
  if (user?.role?.startsWith('b2c_')) {
    return <Navigate to="/b2c" replace />;
  }
  
  // Wait for permissions to load
  if (!permissions) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }
  
  // Check tab access
  if (!hasTabAccess(tabName)) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
          <div className="text-red-500 text-6xl mb-4">⛔</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-4">You do not have permission to access this page.</p>
          <a href="/dashboard" className="text-blue-600 hover:underline">Return to Dashboard</a>
        </div>
      </Layout>
    );
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'mis' ? '/mis' : user?.role === 'mis_turing' ? '/turing/dashboard' : user?.role === 'internal_screening' ? '/screening/dashboard' : user?.role === 'icf' ? '/icf' : user?.role?.startsWith('b2c_') ? '/b2c' : '/dashboard'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
        </PublicRoute>
      } />
      
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      
      {/* Public Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <SmartDashboard />
        </ProtectedRoute>
      } />

      {/* iter223 — LeadsGen role pages + BDE/BDEM team view */}
      <Route path="/leadsgen" element={
        <ProtectedRoute><LeadsGenPage /></ProtectedRoute>
      } />
      <Route path="/icfs" element={
        <ProtectedRoute><ICFsPage /></ProtectedRoute>
      } />
      <Route path="/leadsgen/activity" element={
        <ProtectedRoute><LeadsGenActivity /></ProtectedRoute>
      } />
      <Route path="/team-leadsgen" element={
        <ProtectedRoute><LeadsGenPage /></ProtectedRoute>
      } />

      <Route path="/analysis" element={
        <ProtectedRoute>
          <AnalysisPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/classic" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/recruiter" element={
        <ProtectedRoute>
          <RecruiterDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/manager" element={
        <ProtectedRoute>
          <ManagerDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/candidates" element={
        <TabProtectedRoute tabName="candidates">
          <CandidatesPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/candidates/new" element={
        <TabProtectedRoute tabName="candidates">
          <CandidatesPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/candidates/:id" element={
        <TabProtectedRoute tabName="candidates">
          <CandidateDetailPage />
        </TabProtectedRoute>
      } />

      <Route path="/inhouse-developers" element={
        <ProtectedRoute>
          <InhouseDevelopersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/jobs" element={
        <TabProtectedRoute tabName="jobs">
          <JobsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/jobs/:id" element={
        <TabProtectedRoute tabName="jobs">
          <JobDetailPage />
        </TabProtectedRoute>
      } />

      <Route path="/bde-manager/jobs/:id" element={
        <TabProtectedRoute tabName="jobs">
          <BDEManagerJobDetailPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/bde/job/:jobId/shortlisted-candidates" element={
        <ProtectedRoute>
          <BdeShortlistedCandidatesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/teamlead/job/:jobId/shortlisted-candidates" element={
        <ProtectedRoute>
          <TeamLeadShortlistedCandidatesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <TabProtectedRoute tabName="clients">
          <ClientsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/interviews" element={
        <TabProtectedRoute tabName="interviews">
          <InterviewsPage />
        </TabProtectedRoute>
      } />

      <Route path="/pipeline" element={
        <ProtectedRoute>
          <PipelinePage />
        </ProtectedRoute>
      } />
      
      <Route path="/interviews/new" element={
        <TabProtectedRoute tabName="interviews">
          <InterviewsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/interviews/:id" element={
        <TabProtectedRoute tabName="interviews">
          <InterviewDetailPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/workorders" element={
        <TabProtectedRoute tabName="workorders">
          <WorkordersPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/timesheets" element={
        <TabProtectedRoute tabName="timesheets">
          <TimesheetsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/laptops" element={
        <TabProtectedRoute tabName="laptops">
          <LaptopsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/email-requests" element={
        <TabProtectedRoute tabName="email_requests">
          <EmailRequestsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <TabProtectedRoute tabName="analytics">
          <AnalyticsPage />
        </TabProtectedRoute>
      } />
      
      {/* Redirect /reports to /analytics for combined page */}
      <Route path="/reports" element={<Navigate to="/analytics" replace />} />
      
      <Route path="/team" element={
        <TabProtectedRoute tabName="team">
          <TeamManagementPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/team/:id" element={
        <TabProtectedRoute tabName="team">
          <TeamDetailPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/audit-logs" element={
        <TabProtectedRoute tabName="audit_logs">
          <AuditLogsPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/setup" element={
        <TabProtectedRoute tabName="setup">
          <SetupPage />
        </TabProtectedRoute>
      } />
      
      <Route path="/email-sourcing" element={
        <ProtectedRoute>
          <EmailSourcingPage />
        </ProtectedRoute>
      } />
      <Route path="/gmail-sourcing" element={
        <ProtectedRoute>
          <GmailSourcingPage />
        </ProtectedRoute>
      } />
      <Route path="/gmail-sync-admin" element={
        <TabProtectedRoute tabName="setup">
          <GmailSyncControlCenter />
        </TabProtectedRoute>
      } />
      
      <Route path="/search" element={
        <ProtectedRoute>
          <SemanticSearchPage />
        </ProtectedRoute>
      } />
      
      <Route path="/performance" element={
        <ProtectedRoute>
          <GamificationDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/gamification-admin" element={
        <TabProtectedRoute tabName="setup">
          <AdminGamificationPage />
        </TabProtectedRoute>
      } />
      
      {/* Custom Entity Dynamic Route */}
      <Route path="/entity/:entityName" element={
        <ProtectedRoute>
          <Layout>
            <CustomEntityPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* iter 191 — MIS Portal (isolated shell) */}
      <Route path="/mis" element={
        <MISProtectedRoute>
          <MISLayout />
        </MISProtectedRoute>
      }>
        <Route index element={<MISDashboard />} />
        <Route path="b2b-team" element={<MISB2BTeam />} />
        <Route path="analytics" element={<Navigate to="/mis" replace />} />
        <Route path="leaderboard" element={<MISLeaderboard />} />
        <Route path="b2c-team" element={<MISB2CTeam />} />
        <Route path="turing-team" element={<MISTuringTeam />} />
        <Route path="micro1-team" element={<MISMicro1Team />} />
        <Route path="icf-team" element={<MISICFTeam />} />
        <Route path="internal-screening" element={<MISInternalScreening />} />
        <Route path="leadsgen" element={<MISLeadsGen />} />
      </Route>
      
      {/* iter 203 — MIS Turing role pages (inside RecruitPro shell) */}
      <Route path="/turing/dashboard" element={
        <MISTuringProtectedRoute>
          <TuringDashboardPage />
        </MISTuringProtectedRoute>
      } />
      <Route path="/turing/submissions" element={
        <MISTuringProtectedRoute>
          <TuringSubmissionsPage />
        </MISTuringProtectedRoute>
      } />
      <Route path="/turing-team-view" element={
        <TuringTeamAccessRoute>
          <MISTuringTeam />
        </TuringTeamAccessRoute>
      } />
      <Route path="/turing/micro" element={
        <MISTuringProtectedRoute>
          <MicroSubmissionsPage />
        </MISTuringProtectedRoute>
      } />
      
      {/* iter 205 — Internal Screening role pages (inside RecruitPro shell) */}
      <Route path="/screening/dashboard" element={
        <ScreeningProtectedRoute>
          <ScreeningDashboardPage />
        </ScreeningProtectedRoute>
      } />
      <Route path="/screening/queue" element={
        <ScreeningProtectedRoute>
          <ScreeningPage />
        </ScreeningProtectedRoute>
      } />
      
      {/* iter 202 — ICF Portal (isolated shell) */}
      <Route path="/icf" element={
        <MISProtectedRoute allowRole="icf">
          <ICFLayout />
        </MISProtectedRoute>
      }>
        <Route index element={<ICFDashboard />} />
        {/* iter220 — ICF reuses the exact Team Lead pages (backend scopes data) */}
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:id" element={<JobDetailPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="candidates/:id" element={<CandidateDetailPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        {/* iter263 — ICF Sheet removed from the RecruitPro ICF portal (MIS ICF Team keeps its own sheet) */}
        {/* iter261 — ICF role operates the full ICF's workflow page */}
        <Route path="icfs" element={<ICFsPage />} />
      </Route>
      
      {/* iter 193 — B2C Portal (isolated shell) */}
      <Route path="/b2c" element={
        <B2CProtectedRoute>
          <B2CLayout />
        </B2CProtectedRoute>
      }>
        <Route index element={<B2CDashboard />} />
        <Route path="users" element={<B2CUsers />} />
        <Route path="developers" element={<B2CDevelopers />} />
        <Route path="interviews" element={<B2CInterviews />} />
        <Route path="daily-updates" element={<B2CDailyUpdates />} />
      </Route>
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <SseProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </SseProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
